/**
 * QDayClock.jsx — Phase 15.1 / M5 — public Q-Day Tax Counter page.
 *
 * Lives at /q-day on qortrace.com. Full-bleed dark page with a
 * massive animated counter. Interpolates between API ticks using
 * the rate constants returned by /api/public/qday-tax so the counter
 * moves every animation frame (60 fps) without hammering the server.
 *
 * Also surfaces a 1-click embed-code generator so anyone can paste
 * the widget on their docs / partner site (drives QorTrace brand
 * impressions).
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Activity, Coins, FileSearch, ExternalLink, Copy, Check,
  AlertTriangle, Loader2,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────────────────────────────────────────────── */
/*  Hook: live ticker — fetch once, interpolate per frame              */
/* ─────────────────────────────────────────────────────────────────── */

function useQDayTax() {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const fetchedAtRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/qday-tax`);
      setSnapshot(data);
      fetchedAtRef.current = Date.now();
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load Q-Day Tax counter.");
    }
  };

  useEffect(() => {
    fetchOnce();
    // Re-sync every 60s to keep the interpolation honest.
    const id = setInterval(fetchOnce, 60_000);
    return () => clearInterval(id);
  }, []);

  return { snapshot, error, fetchedAtRef, refresh: fetchOnce };
}


/* ─────────────────────────────────────────────────────────────────── */
/*  Animated number — interpolates locally between fetches              */
/* ─────────────────────────────────────────────────────────────────── */

function LiveNumber({ baseline, ratePerSecond, fetchedAtRef, formatter, testid }) {
  const [value, setValue] = useState(baseline);
  const baselineRef = useRef(baseline);
  const rateRef = useRef(ratePerSecond);

  // Sync refs whenever new server snapshot arrives.
  useEffect(() => {
    baselineRef.current = baseline;
    rateRef.current = ratePerSecond;
    setValue(baseline);
  }, [baseline, ratePerSecond]);

  useEffect(() => {
    let raf;
    const tick = () => {
      const elapsedLocalS = (Date.now() - (fetchedAtRef.current || Date.now())) / 1000;
      setValue(baselineRef.current + elapsedLocalS * rateRef.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fetchedAtRef]);

  return (
    <span data-testid={testid} className="tabular-nums">
      {formatter(value)}
    </span>
  );
}


/* ─────────────────────────────────────────────────────────────────── */
/*  Formatters                                                         */
/* ─────────────────────────────────────────────────────────────────── */

const fmtUSD = (n) =>
  "$" + Math.floor(n).toLocaleString("en-US");

const fmtBytes = (n) => {
  // PB-scale comfort: show GB then TB then PB then EB.
  const units = [
    { factor: 1e18, label: " EB" },
    { factor: 1e15, label: " PB" },
    { factor: 1e12, label: " TB" },
    { factor: 1e9,  label: " GB" },
    { factor: 1e6,  label: " MB" },
  ];
  for (const u of units) {
    if (n >= u.factor) {
      return (n / u.factor).toFixed(2) + u.label;
    }
  }
  return Math.floor(n).toLocaleString("en-US") + " B";
};

const fmtCount = (n) => Math.floor(n).toLocaleString("en-US");


/* ─────────────────────────────────────────────────────────────────── */
/*  Page                                                               */
/* ─────────────────────────────────────────────────────────────────── */

export default function QDayClock() {
  usePageMeta(M.qDay);
  const { snapshot, error, fetchedAtRef, refresh } = useQDayTax();
  const [copied, setCopied] = useState(false);

  const embedSrc = `${API || "https://qortrace.com"}/widget/q-day-counter`;
  const embedCode = `<iframe src="${embedSrc}" width="100%" height="240" style="border:0;background:transparent" loading="lazy" title="Q-Day Tax Counter by QorTrace"></iframe>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* swallow */
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#070A12] text-white">
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <section
          className="relative isolate overflow-hidden border-b border-white/5"
          data-testid="qday-clock-hero"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 0%, rgba(245,158,11,0.18), transparent 60%), radial-gradient(45% 45% at 80% 70%, rgba(31,221,131,0.12), transparent 60%)",
            }}
          />
          <div className="relative mx-auto max-w-6xl px-6 py-16 sm:py-24">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-300">
              <Activity className="h-3.5 w-3.5" /> Live · since FIPS 203/204/205 finalised
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              The Q-Day Tax Clock
            </h1>
            <p className="mt-4 max-w-2xl text-base text-gray-300 sm:text-lg">
              Every second your stack stays on classical PKI, the world's
              quantum-readiness debt grows. This is the running ticker —
              cited, deterministic, and live since NIST finalised the
              post-quantum standards on{" "}
              <span className="text-white">13 August 2024</span>.
            </p>

            {error && (
              <div
                className="mt-8 flex items-center gap-2 rounded-md border border-rose-700 bg-rose-900/30 p-4 text-sm text-rose-200"
                data-testid="qday-clock-error"
              >
                <AlertTriangle className="h-4 w-4" />
                {error}
                <button onClick={refresh} className="ml-auto text-xs underline">retry</button>
              </div>
            )}

            {!snapshot && !error && (
              <div className="mt-12 flex items-center gap-2 text-gray-400" data-testid="qday-clock-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading live ticker…
              </div>
            )}

            {snapshot && (
              <>
                {/* The headline number — encryption debt */}
                <div className="mt-12">
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-300/80">
                    Global encryption debt
                  </div>
                  <div
                    className="mt-2 font-mono text-5xl font-bold text-amber-300 sm:text-7xl lg:text-8xl"
                    style={{ fontFamily: '"IBM Plex Mono", monospace', textShadow: "0 0 28px rgba(245,158,11,0.30)" }}
                  >
                    <LiveNumber
                      baseline={snapshot.encryption_debt_usd}
                      ratePerSecond={snapshot.rates.encryption_debt_usd_per_second}
                      fetchedAtRef={fetchedAtRef}
                      formatter={fmtUSD}
                      testid="qday-debt-usd"
                    />
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    accruing at ${snapshot.rates.encryption_debt_usd_per_second.toFixed(2)} / sec
                  </div>
                </div>

                {/* Secondary counters */}
                <div className="mt-10 grid gap-6 sm:grid-cols-2">
                  <div className="rounded-md border border-white/5 bg-white/[0.03] p-6" data-testid="qday-card-bytes">
                    <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-rose-300/80">
                      <FileSearch className="h-3.5 w-3.5" /> HNDL traffic captured
                    </div>
                    <div className="font-mono text-3xl font-bold text-rose-300 sm:text-4xl"
                         style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      <LiveNumber
                        baseline={snapshot.bytes_harvested}
                        ratePerSecond={snapshot.rates.bytes_per_second}
                        fetchedAtRef={fetchedAtRef}
                        formatter={fmtBytes}
                        testid="qday-bytes"
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      classical-PKI ciphertext on the wire, ~1.5% of global Internet flow
                    </div>
                  </div>
                  <div className="rounded-md border border-white/5 bg-white/[0.03] p-6" data-testid="qday-card-records">
                    <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-300/80">
                      <Coins className="h-3.5 w-3.5" /> Long-life records at risk
                    </div>
                    <div className="font-mono text-3xl font-bold text-cyan-300 sm:text-4xl"
                         style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      <LiveNumber
                        baseline={snapshot.records_at_risk}
                        ratePerSecond={snapshot.rates.records_per_second}
                        fetchedAtRef={fetchedAtRef}
                        formatter={fmtCount}
                        testid="qday-records"
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      records on classical PKI (RSA / ECC) likely to outlive Q-Day
                    </div>
                  </div>
                </div>

                {/* CTAs */}
                <div className="mt-10 flex flex-wrap gap-3" data-testid="qday-ctas">
                  <Link
                    to="/free-scan"
                    className="inline-flex items-center gap-2 rounded-md bg-[#1FDD83] px-5 py-3 text-sm font-bold uppercase tracking-wider text-black transition-opacity hover:opacity-90"
                    data-testid="qday-cta-scan"
                  >
                    Run a free PQC scan <ExternalLink className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/pricing/enterprise"
                    className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-transparent px-5 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-white/30"
                    data-testid="qday-cta-enterprise"
                  >
                    PQC audit suite
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── Embed code generator ─────────────────────────────────── */}
        {snapshot && (
          <section className="border-b border-white/5 py-16" data-testid="qday-embed-section">
            <div className="mx-auto max-w-6xl px-6">
              <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1FDD83]">
                    Embed the clock
                  </div>
                  <h2 className="mt-2 text-lg font-bold sm:text-2xl">
                    Drop the Q-Day Tax counter into any page.
                  </h2>
                  <p className="mt-3 text-sm text-gray-400">
                    Transparent background, no tracking, no auth. Use it on
                    your security docs, partner sites, or board-deck microsite
                    to make Q-Day risk feel real. Each render counts toward
                    your QorTrace brand impressions.
                  </p>
                </div>
                <div className="rounded-md border border-white/10 bg-black/40 p-5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
                      Embed snippet
                    </span>
                    <button
                      onClick={copyEmbed}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#1FDD83]/40 bg-[#1FDD83]/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-[#1FDD83] transition-colors hover:bg-[#1FDD83]/20"
                      data-testid="qday-embed-copy"
                    >
                      {copied ? (<><Check className="h-3 w-3" /> Copied</>) : (<><Copy className="h-3 w-3" /> Copy</>)}
                    </button>
                  </div>
                  <code
                    className="block max-h-32 overflow-y-auto whitespace-pre-wrap break-all rounded bg-black/60 p-3 text-xs text-gray-300"
                    style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    data-testid="qday-embed-code"
                  >
                    {embedCode}
                  </code>
                  <div className="mt-4">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                      Preview
                    </div>
                    <div className="rounded-md border border-white/10">
                      <iframe
                        src={embedSrc}
                        width="100%"
                        height="240"
                        style={{ border: 0, background: "transparent" }}
                        loading="lazy"
                        title="Q-Day Tax Counter preview"
                        data-testid="qday-embed-preview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Methodology / sources ───────────────────────────────── */}
        {snapshot && (
          <section className="py-16" data-testid="qday-methodology">
            <div className="mx-auto max-w-6xl px-6">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Methodology
              </div>
              <h2 className="mt-2 text-lg font-bold sm:text-2xl">
                Where the numbers come from
              </h2>
              <p className="mt-3 max-w-3xl text-sm text-gray-400">
                Every accrual rate is publicly cited and intentionally conservative.
                Policy version{" "}
                <code className="rounded bg-gray-800 px-1.5 py-0.5 text-[#1FDD83]">{snapshot.policy_version}</code>{" "}
                — accrual constants are listed below for full reproducibility.
              </p>
              <ul className="mt-6 grid gap-3 text-sm text-gray-300 sm:grid-cols-2">
                {snapshot.sources.map((s) => (
                  <li key={s.id} className="rounded-md border border-white/5 bg-white/[0.02] p-4">
                    <a
                      href={s.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-start gap-2 text-gray-200 hover:text-white"
                      data-testid={`qday-source-${s.id}`}
                    >
                      <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                      <span>{s.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-6 grid gap-3 text-xs text-gray-500 sm:grid-cols-3">
                <div>
                  Bytes/sec: <span className="text-gray-200">{snapshot.rates.bytes_per_second.toLocaleString("en-US")}</span>
                </div>
                <div>
                  Debt/sec USD: <span className="text-gray-200">${snapshot.rates.encryption_debt_usd_per_second.toFixed(4)}</span>
                </div>
                <div>
                  Records/sec: <span className="text-gray-200">{snapshot.rates.records_per_second.toLocaleString("en-US")}</span>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
